import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSupabaseClient } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { LoadingState, ErrorState } from "@/components/states";
import { toast } from "@/lib/toast";
import {
  Check,
  X as XIcon,
  MagnifyingGlass as Search,
  Copy as CopyIcon,
  Lightning,
} from "@phosphor-icons/react";
import {
  fetchBetaSignups,
  approveBetaSignup,
  rejectBetaSignup,
  approveAllPending,
  setBetaFormEnabled,
  isBetaFormEnabled,
  setBetaAutoApprove,
  isBetaAutoApproveEnabled,
  type BetaSignupRow,
} from "../api/betaSignups";

type StatusFilter = "all" | "pending" | "approved" | "rejected";

const STATUS_BADGE: Record<BetaSignupRow["status"], { label: string; cls: string }> = {
  pending:  { label: "Pendente",  cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  approved: { label: "Aprovado",  cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  rejected: { label: "Rejeitado", cls: "bg-destructive/15 text-red-400 border-destructive/30" },
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function BetaSignupsSection() {
  const { client, isReady } = useSupabaseClient();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("pending");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const { data: signups = [], isLoading, error } = useQuery({
    queryKey: ["admin", "beta_signups", isReady],
    queryFn: () => fetchBetaSignups(client),
    enabled: isReady,
    refetchInterval: 30_000,
  });

  const { data: formEnabled } = useQuery({
    queryKey: ["admin", "beta_form_enabled", isReady],
    queryFn: () => isBetaFormEnabled(client),
    enabled: isReady,
  });

  const { data: autoApprove } = useQuery({
    queryKey: ["admin", "beta_auto_approve", isReady],
    queryFn: () => isBetaAutoApproveEnabled(client),
    enabled: isReady,
  });

  const approveMut = useMutation({
    mutationFn: (id: string) => approveBetaSignup(client, id),
    onSuccess: (res) => {
      if (res.emailSent) {
        toast.success("Acesso liberado e convite enviado.");
      } else if (res.alreadyUser) {
        toast.success("Acesso liberado. Usuário já tinha conta — sem email novo.");
      } else if (res.error) {
        toast.warning(`Acesso liberado, mas convite falhou: ${res.error}`);
      } else {
        toast.success("Acesso liberado.");
      }
      qc.invalidateQueries({ queryKey: ["admin", "beta_signups"] });
      qc.invalidateQueries({ queryKey: ["admin", "user_activity"] });
    },
    onError: () => toast.error("Falha ao aprovar."),
  });

  const rejectMut = useMutation({
    mutationFn: (id: string) => rejectBetaSignup(client, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "beta_signups"] }),
  });

  const approveAllMut = useMutation({
    mutationFn: () => approveAllPending(client),
    onSuccess: (r) => {
      toast.success(
        `${r.approved} aprovado(s) · ${r.emailsSent} email(s) enviado(s)` +
          (r.alreadyUsers ? ` · ${r.alreadyUsers} já tinha conta` : "") +
          (r.failed ? ` · ${r.failed} falha(s)` : ""),
      );
      qc.invalidateQueries({ queryKey: ["admin", "beta_signups"] });
      qc.invalidateQueries({ queryKey: ["admin", "user_activity"] });
    },
    onError: () => toast.error("Falha ao aprovar em lote."),
  });

  const bulkApproveMut = useMutation({
    mutationFn: async (ids: string[]) => {
      let approved = 0;
      let emailsSent = 0;
      let alreadyUsers = 0;
      let failed = 0;
      for (const id of ids) {
        try {
          const res = await approveBetaSignup(client, id);
          approved += 1;
          if (res.emailSent) emailsSent += 1;
          if (res.alreadyUser) alreadyUsers += 1;
          if (res.error) failed += 1;
        } catch {
          failed += 1;
        }
      }
      return { approved, emailsSent, alreadyUsers, failed };
    },
    onSuccess: (r) => {
      toast.success(
        `${r.approved} aprovado(s) · ${r.emailsSent} email(s) enviado(s)` +
          (r.alreadyUsers ? ` · ${r.alreadyUsers} já tinha conta` : "") +
          (r.failed ? ` · ${r.failed} falha(s)` : ""),
      );
      setSelectedIds(new Set());
      qc.invalidateQueries({ queryKey: ["admin", "beta_signups"] });
      qc.invalidateQueries({ queryKey: ["admin", "user_activity"] });
    },
    onError: () => toast.error("Falha ao aprovar selecionados."),
  });

  const toggleFormMut = useMutation({
    mutationFn: (enabled: boolean) => setBetaFormEnabled(client, enabled),
    onSuccess: (_, enabled) => {
      toast.success(enabled ? "Formulário aberto." : "Formulário fechado.");
      qc.invalidateQueries({ queryKey: ["admin", "beta_form_enabled"] });
    },
    onError: () => toast.error("Falha ao alterar status do formulário."),
  });

  const toggleAutoMut = useMutation({
    mutationFn: (enabled: boolean) => setBetaAutoApprove(client, enabled),
    onSuccess: (_, enabled) => {
      toast.success(
        enabled ? "Auto-aprovação ativada." : "Auto-aprovação desativada.",
      );
      qc.invalidateQueries({ queryKey: ["admin", "beta_auto_approve"] });
    },
    onError: () => toast.error("Falha ao alterar auto-aprovação."),
  });

  const filtered = useMemo(() => {
    return signups.filter((s) => {
      const matchesStatus = statusFilter === "all" || s.status === statusFilter;
      const matchesSearch =
        !search ||
        s.email.toLowerCase().includes(search.toLowerCase()) ||
        s.full_name.toLowerCase().includes(search.toLowerCase());
      return matchesStatus && matchesSearch;
    });
  }, [signups, search, statusFilter]);

  const counts = useMemo(() => ({
    all: signups.length,
    pending: signups.filter((s) => s.status === "pending").length,
    approved: signups.filter((s) => s.status === "approved").length,
    rejected: signups.filter((s) => s.status === "rejected").length,
  }), [signups]);

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((s) => selectedIds.has(s.id));

  function toggleAllFiltered() {
    const next = new Set(selectedIds);
    if (allFilteredSelected) {
      filtered.forEach((s) => next.delete(s.id));
    } else {
      filtered.forEach((s) => next.add(s.id));
    }
    setSelectedIds(next);
  }

  function toggleOne(id: string, checked: boolean) {
    const next = new Set(selectedIds);
    if (checked) next.add(id);
    else next.delete(id);
    setSelectedIds(next);
  }

  function copyEmails() {
    const emails = filtered.map((s) => s.email).join("\n");
    navigator.clipboard.writeText(emails).then(() => {
      toast.success(`${filtered.length} email(s) copiado(s).`);
    });
  }

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState error={error} />;

  const selectedCount = selectedIds.size;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Inscrições Beta</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Aprovar libera o acesso. Usuários só aparecem na aba Usuários após concluírem cadastro.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 rounded-lg border border-border/50 bg-card px-3 py-2">
            <Switch
              id="beta-form-toggle"
              checked={formEnabled ?? true}
              onCheckedChange={(v) => toggleFormMut.mutate(v)}
              disabled={toggleFormMut.isPending}
            />
            <Label htmlFor="beta-form-toggle" className="text-xs cursor-pointer">
              {formEnabled === false ? "Formulário fechado" : "Formulário aberto"}
            </Label>
          </div>
          <div className="flex items-center gap-2 rounded-lg border border-border/50 bg-card px-3 py-2">
            <Switch
              id="beta-auto-toggle"
              checked={autoApprove ?? false}
              onCheckedChange={(v) => toggleAutoMut.mutate(v)}
              disabled={toggleAutoMut.isPending}
            />
            <Label htmlFor="beta-auto-toggle" className="text-xs cursor-pointer">
              {autoApprove ? "Auto-aprovação ON" : "Auto-aprovação OFF"}
            </Label>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input
            placeholder="Buscar nome ou email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 text-sm w-64"
          />
        </div>
        <div className="flex gap-1.5">
          {(["pending", "approved", "rejected", "all"] as const).map((s) => (
            <Button
              key={s}
              size="sm"
              variant={statusFilter === s ? "default" : "outline"}
              className="h-7 text-xs"
              onClick={() => setStatusFilter(s)}
            >
              {s === "all"
                ? `Todos (${counts.all})`
                : `${STATUS_BADGE[s].label} (${counts[s]})`}
            </Button>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-2">
          {selectedCount > 0 && (
            <Button
              size="sm"
              className="h-7 gap-1 text-xs"
              disabled={bulkApproveMut.isPending}
              onClick={() => bulkApproveMut.mutate(Array.from(selectedIds))}
            >
              <Check className="w-3 h-3" /> Liberar {selectedCount} selecionados
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            className="h-7 gap-1 text-xs"
            onClick={copyEmails}
            disabled={filtered.length === 0}
          >
            <CopyIcon className="w-3 h-3" /> Copiar emails
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7 gap-1 text-xs text-emerald-400 border-emerald-500/40 hover:bg-emerald-500/10"
            onClick={() => approveAllMut.mutate()}
            disabled={approveAllMut.isPending || counts.pending === 0}
          >
            <Lightning className="w-3 h-3" /> Liberar todos pendentes
          </Button>
        </div>
      </div>

      <div className="border border-border/40 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-muted/30">
            <tr>
              <th className="px-3 py-2 w-8">
                <Checkbox
                  checked={allFilteredSelected}
                  onCheckedChange={() => toggleAllFiltered()}
                  aria-label="Selecionar todos visíveis"
                />
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Nome</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Email</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Compromisso</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Status</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Inscrição</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-sm text-muted-foreground">
                  Nenhuma inscrição encontrada.
                </td>
              </tr>
            ) : (
              filtered.map((s) => {
                const badge = STATUS_BADGE[s.status];
                return (
                  <tr key={s.id} className="border-t border-border/30 hover:bg-muted/20">
                    <td className="px-3 py-2.5">
                      <Checkbox
                        checked={selectedIds.has(s.id)}
                        onCheckedChange={(v) => toggleOne(s.id, Boolean(v))}
                        aria-label="Selecionar"
                      />
                    </td>
                    <td className="px-3 py-2.5 text-sm text-foreground">{s.full_name}</td>
                    <td className="px-3 py-2.5 text-sm font-mono text-foreground/80">{s.email}</td>
                    <td className="px-3 py-2.5">
                      {s.committed ? (
                        <Check className="w-4 h-4 text-emerald-400" />
                      ) : (
                        <XIcon className="w-4 h-4 text-muted-foreground" />
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <Badge className={badge.cls}>{badge.label}</Badge>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground whitespace-nowrap">
                      {formatDate(s.created_at)}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex gap-1.5">
                        {s.status !== "approved" && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 gap-1 text-xs text-emerald-400 border-emerald-500/40 hover:bg-emerald-500/10"
                            disabled={approveMut.isPending}
                            onClick={() => approveMut.mutate(s.id)}
                          >
                            <Check className="w-3 h-3" /> Liberar acesso
                          </Button>
                        )}
                        {s.status !== "rejected" && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 gap-1 text-xs text-destructive border-destructive/40 hover:bg-destructive/10"
                            disabled={rejectMut.isPending}
                            onClick={() => rejectMut.mutate(s.id)}
                          >
                            <XIcon className="w-3 h-3" /> Rejeitar
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
